import type { HealthBarBand } from '../../api/health-bar'
import { RHYTHM_STYLE } from '../../lib/rhythm-style'

interface RhythmAvatarProps {
  /** Person's name — the source of the initials shown in the square. */
  name: string
  band: HealthBarBand
  /** Active pattern count, surfaced in the hover title for context. */
  patternCount?: number
  /** Outer size in px. Defaults to 34 (roster-row scale). */
  size?: number
}

/** Two-letter initials from a name: "Ravi Subra" → "RS", "Cher" → "CH". */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

/**
 * Rounded-square avatar showing the person's initials, tinted by their
 * leave-rhythm band (soft fill + band-coloured text) — the same look the
 * Rhythm board roster uses. Reads the person's rhythm colour at a glance
 * while still identifying them by initials (not a status glyph).
 */
export function RhythmAvatar({
  name,
  band,
  patternCount = 0,
  size = 34,
}: RhythmAvatarProps): JSX.Element {
  const style = RHYTHM_STYLE[band]
  const title =
    patternCount === 0
      ? `Leave rhythm · ${style.label}`
      : `Leave rhythm · ${style.label} · ${patternCount} active pattern${patternCount === 1 ? '' : 's'}`
  return (
    <div
      data-testid="rhythm-avatar"
      data-band={band}
      title={title}
      aria-label={`${name} · ${title}`}
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: Math.max(6, Math.round(size * 0.28)),
        background: style.fill,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: style.ring,
        fontSize: Math.round(size * 0.32),
        fontWeight: 800,
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      <span aria-hidden="true">{initialsOf(name)}</span>
    </div>
  )
}
